import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
  Statistic,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import {
  PlusOutlined,
  ReloadOutlined,
  TagOutlined,
  MoneyCollectOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import RobinPage from "../components/RobinPage";
import EditDeleteActions from "../components/EditDeleteActions";
import {
  useGetAllPromoCodesQuery,
  useGetAllStoresQuery,
  useCreatePromoCodeMutation,
  useUpdatePromoCodeMutation,
  useDeletePromoCodeMutation,
  GetAllPromoCodesDocument,
} from "../graphql/generated/hooks";
import { PromoCodeDiscountType } from "../graphql/generated/schemas";
import dayjs from "dayjs";
import { parseArabicNumbers } from "../common/util";

interface PromoCodeRow {
  key: number;
  id: number;
  code: string;
  discountType: PromoCodeDiscountType;
  discountValue: number;
  usageCount: number;
  usageLimit: number | null;
  isActive: boolean;
  storeId: number | null;
  partnerName: string;
  partnerNameAr: string;
  storeName: string;
  storeNameAr: string;
}

function PartnerPromoCodesPage() {
  const { t, i18n } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const { data, loading, refetch } = useGetAllPromoCodesQuery({
    fetchPolicy: "cache-and-network",
  });

  const { data: storesData } = useGetAllStoresQuery();

  const [createPromoCode, { loading: creating }] =
    useCreatePromoCodeMutation();
  const [updatePromoCode, { loading: updating }] =
    useUpdatePromoCodeMutation();
  const [deletePromoCode, { loading: deleting }] =
    useDeletePromoCodeMutation();

  const allCodes = data?.getAllPromoCodes ?? [];
  const allStores = storesData?.getAllStores ?? [];

  const storeOptions = allStores.map((s) => ({
    label: `${s.name} (${s.nameAr}) — ${s.partner.name}`,
    value: s.id,
  }));

  const stats = useMemo(() => {
    const partnerMap = new Map<number, { name: string; nameAr: string }>();
    allCodes.forEach((pc) => {
      if (pc.store?.partner) {
        partnerMap.set(pc.store.partner.id, {
          name: pc.store.partner.name,
          nameAr: pc.store.partner.nameAr,
        });
      }
    });
    return {
      totalPartners: partnerMap.size,
      totalCodes: allCodes.length,
      activeCodes: allCodes.filter((pc) => pc.isActive).length,
      totalUsages: allCodes.reduce((sum, pc) => sum + pc.usageCount, 0),
    };
  }, [allCodes]);

  const dataSource: PromoCodeRow[] = allCodes.map((pc) => ({
    key: pc.id,
    id: pc.id,
    code: pc.code,
    discountType: pc.discountType,
    discountValue: pc.discountValue,
    usageCount: pc.usageCount,
    usageLimit: pc.usageLimit ?? null,
    isActive: pc.isActive,
    storeId: pc.storeId ?? null,
    partnerName: pc.store?.partner?.name ?? "—",
    partnerNameAr: pc.store?.partner?.nameAr ?? "—",
    storeName: pc.store?.name ?? "—",
    storeNameAr: pc.store?.nameAr ?? "—",
  }));

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      discountType: PromoCodeDiscountType.Percentage,
      discountValue: 10,
      perUserLimit: 1,
      isActive: true,
    });
    setDrawerOpen(true);
  };

  const openEdit = (row: PromoCodeRow) => {
    setEditingId(row.id);
    const code = allCodes.find((pc) => pc.id === row.id);
    if (code) {
      form.setFieldsValue({
        code: code.code,
        description: code.description,
        discountType: code.discountType,
        discountValue: code.discountValue,
        minCartAmount: code.minCartAmount,
        maxDiscountAmount: code.maxDiscountAmount,
        usageLimit: code.usageLimit,
        perUserLimit: code.perUserLimit ?? 1,
        startsAt: code.startsAt ? dayjs(code.startsAt) : undefined,
        endsAt: code.endsAt ? dayjs(code.endsAt) : undefined,
        isActive: code.isActive,
        storeId: code.storeId,
      });
    }
    setDrawerOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const variables: {
        code: string;
        discountType: PromoCodeDiscountType;
        discountValue: number;
        isActive: boolean;
        perUserLimit: number;
        description?: string;
        storeId?: number;
        minCartAmount?: number;
        maxDiscountAmount?: number;
        usageLimit?: number;
        startsAt?: string;
        endsAt?: string;
      } = {
        code: values.code?.trim().toUpperCase(),
        discountType: values.discountType,
        discountValue: values.discountValue,
        isActive: values.isActive ?? true,
        perUserLimit: values.perUserLimit ?? 1,
      };

      if (values.description?.trim()) {
        variables.description = values.description.trim();
      }
      if (values.storeId) {
        variables.storeId = values.storeId;
      }
      if (values.minCartAmount != null && values.minCartAmount !== 0) {
        variables.minCartAmount = values.minCartAmount;
      }
      if (values.maxDiscountAmount != null && values.maxDiscountAmount !== 0) {
        variables.maxDiscountAmount = values.maxDiscountAmount;
      }
      if (values.usageLimit != null) {
        variables.usageLimit = values.usageLimit;
      }
      if (values.startsAt) {
        variables.startsAt = dayjs(values.startsAt).toISOString();
      }
      if (values.endsAt) {
        variables.endsAt = dayjs(values.endsAt).toISOString();
      }

      if (editingId) {
        await updatePromoCode({
          variables: { id: editingId, updatePromoCodeDto: variables },
          refetchQueries: [{ query: GetAllPromoCodesDocument }],
          awaitRefetchQueries: true,
        });
        messageApi.success(t("message.success.data_updated"));
      } else {
        await createPromoCode({
          variables: { createPromoCodeDto: variables },
          refetchQueries: [{ query: GetAllPromoCodesDocument }],
          awaitRefetchQueries: true,
        });
        messageApi.success(t("message.success.data_created"));
      }
      setDrawerOpen(false);
    } catch (err: any) {
      if (err?.errorFields) return;
      messageApi.error(err?.message || "Failed to save promo code");
    }
  };

  const handleDelete = (id: number) => {
    deletePromoCode({
      variables: { id },
      refetchQueries: [{ query: GetAllPromoCodesDocument }],
      awaitRefetchQueries: true,
    })
      .then(() => messageApi.success(t("message.success.data_deleted")))
      .catch(() => messageApi.error(t("message.error.data_delete_failed")));
  };

  const columns: ColumnsType<PromoCodeRow> = [
    {
      title: t("label.partner.name") || "Partner",
      dataIndex: "partnerName",
      key: "partnerName",
      render: (_: any, row: PromoCodeRow) => (
        <span>
          {row.partnerName}{" "}
          <Typography.Text type="secondary">
            ({row.partnerNameAr})
          </Typography.Text>
        </span>
      ),
    },
    {
      title: t("label.promo_code.code"),
      dataIndex: "code",
      key: "code",
      render: (code: string) => (
        <Typography.Text strong copyable>
          {code}
        </Typography.Text>
      ),
    },

    {
      title: t("label.promo_code.discount_type"),
      dataIndex: "discountType",
      key: "discountType",
      render: (discountType: PromoCodeDiscountType) =>
        discountType === PromoCodeDiscountType.Percentage
          ? t("label.promo_code.percentage")
          : t("label.promo_code.fixed"),
    },
    {
      title: t("label.promo_code.discount_value"),
      dataIndex: "discountValue",
      key: "discountValue",
      render: (value: number, record: PromoCodeRow) =>
        `${value}${
          record.discountType === PromoCodeDiscountType.Percentage
            ? "%"
            : ` ${t("label.currency")}`
        }`,
    },
    {
      title: t("label.promo_code.usage_count"),
      key: "usage",
      render: (_: any, row: PromoCodeRow) =>
        row.usageLimit != null
          ? `${row.usageCount} / ${row.usageLimit}`
          : String(row.usageCount),
    },
    {
      title: t("label.promo_code.is_active"),
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) =>
        isActive ? (
          <Tag color="green">{t("button.activate")}</Tag>
        ) : (
          <Tag color="red">{t("button.deactivate")}</Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, row: PromoCodeRow) => (
        <EditDeleteActions
          onEdit={() => openEdit(row)}
          onDelete={() => handleDelete(row.id)}
          loadingDelete={deleting}
        />
      ),
    },
  ];

  const isSaving = creating || updating;

  return (
    <RobinPage
      title={t("label.menu.partner_promo_codes")}
      actions={
        <Space size="large">
          <Typography.Link onClick={() => refetch()}>
            <ReloadOutlined />
          </Typography.Link>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t("button.add")}
          </Button>
        </Space>
      }
    >
      {contextHolder}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title={t("label.partner.name") || "Partners"}
              value={stats.totalPartners}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title={t("label.menu.promo_codes") || "Total Codes"}
              value={stats.totalCodes}
              prefix={<TagOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title={t("label.tracking.active_codes") || "Active"}
              value={stats.activeCodes}
              prefix={<TagOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title={t("label.tracking.total_usages") || "Total Usages"}
              value={stats.totalUsages}
              prefix={<MoneyCollectOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} codes`,
        }}
      />

      <Drawer
        title={
          editingId ? t("title.edit_promo_code") : t("title.add_promo_code")
        }
        width={640}
        open={drawerOpen}
        onClose={() => !isSaving && setDrawerOpen(false)}
        footer={
          <Space style={{ float: "right" }}>
            <Button onClick={() => setDrawerOpen(false)} disabled={isSaving}>
              {t("button.cancel")}
            </Button>
            <Button type="primary" onClick={handleSubmit} loading={isSaving}>
              {t("button.save")}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label={t("label.promo_code.code")}
                rules={[{ required: true, message: "Required" }]}
              >
                <Input
                  maxLength={50}
                  style={{ textTransform: "uppercase" }}
                  placeholder="SUMMER10"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="storeId"
                label={t("label.promo_code.store_id") || "Store"}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select store"
                  options={storeOptions}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label={t("label.promo_code.description")}
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="discountType"
                label={t("label.promo_code.discount_type")}
                initialValue={PromoCodeDiscountType.Percentage}
                rules={[{ required: true, message: "Required" }]}
              >
                <Select
                  options={[
                    {
                      label: t("label.promo_code.percentage"),
                      value: PromoCodeDiscountType.Percentage,
                    },
                    {
                      label: t("label.promo_code.fixed"),
                      value: PromoCodeDiscountType.Fixed,
                    },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const isPct =
                    getFieldValue("discountType") ===
                    PromoCodeDiscountType.Percentage;
                  return (
                    <Form.Item
                      name="discountValue"
                      label={t("label.promo_code.discount_value")}
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <InputNumber
                        min={0.01}
                        max={isPct ? 100 : undefined}
                        precision={2}
                        addonAfter={isPct ? "%" : t("label.currency")}
                        style={{ width: "100%" }}
                        parser={parseArabicNumbers}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="isActive"
                label={t("label.promo_code.is_active")}
                valuePropName="checked"
                initialValue={true}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="minCartAmount"
                label={t("label.promo_code.min_cart_amount")}
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: "100%" }}
                  parser={parseArabicNumbers}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const isFixed =
                    getFieldValue("discountType") ===
                    PromoCodeDiscountType.Fixed;
                  return (
                    <Form.Item
                      name="maxDiscountAmount"
                      label={t("label.promo_code.max_discount_amount")}
                      rules={
                        isFixed
                          ? [
                              {
                                validator: (_, value) => {
                                  if (value == null || value === 0)
                                    return Promise.resolve();
                                  const dv = getFieldValue("discountValue");
                                  if (dv != null && value > dv) {
                                    return Promise.reject(
                                      new Error(
                                        t("validation.max_discount_exceeds_value", {
                                          defaultValue:
                                            "maxDiscountAmount cannot exceed discountValue for FIXED codes",
                                        }),
                                      ),
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]
                          : []
                      }
                    >
                      <InputNumber
                        min={0}
                        precision={2}
                        style={{ width: "100%" }}
                        parser={parseArabicNumbers}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="usageLimit"
                label={t("label.promo_code.usage_limit")}
              >
                <InputNumber
                  min={1}
                  precision={0}
                  style={{ width: "100%" }}
                  parser={parseArabicNumbers}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="perUserLimit"
                label={t("label.promo_code.per_user_limit")}
                initialValue={1}
              >
                <InputNumber
                  min={1}
                  precision={0}
                  style={{ width: "100%" }}
                  parser={parseArabicNumbers}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="startsAt"
                label={t("label.promo_code.starts_at")}
              >
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="endsAt" label={t("label.promo_code.ends_at")}>
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </RobinPage>
  );
}

export default PartnerPromoCodesPage;
